import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { externalUsersApi } from '@/features/external-users/api/externalUsersApi';
import type { AuditLog } from '@/features/external-users/types/audit-log.types';

/**
 * Hooks React Query partages pour les utilisateurs externes
 * Utilises par admin et outil-pro
 * Les mutations n'incluent PAS de notifications — chaque consommateur gere les siennes
 */

// ==================== QUERIES ====================

export const useExternalUsers = (filters: Record<string, any> = {}) => {
  return useQuery({
    queryKey: ['external-users', filters],
    queryFn: () => externalUsersApi.getAll(filters),
  });
};

export const useExternalUser = (id: string) => {
  return useQuery({
    queryKey: ['external-user', id],
    queryFn: () => externalUsersApi.getOne(id),
    enabled: !!id,
  });
};

export const useExternalUserAuditLogs = (userId: string, limit = 50) => {
  return useQuery<AuditLog[]>({
    queryKey: ['external-user-audit-logs', userId, limit],
    queryFn: () => externalUsersApi.getAuditLogs(userId, limit),
    enabled: !!userId,
    staleTime: 10000,
  });
};

// ==================== MUTATIONS (sans notifications) ====================

export const useToggleExternalUserStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => externalUsersApi.toggleStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['external-users'] });
    },
  });
};

export const useCreateExternalUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: externalUsersApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['external-users'] });
    },
  });
};

export const useUpdateExternalUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      externalUsersApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['external-user', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['external-users'] });
    },
  });
};

export const useDeleteExternalUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      externalUsersApi.delete(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['external-users'] });
    },
  });
};

export const useUpdateUserPermissions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, permissions }: { id: string; permissions: string[] }) =>
      externalUsersApi.updatePermissions(id, permissions),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['external-user', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['external-users'] });
    },
  });
};

export const useSetPasswordForExternalUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      externalUsersApi.setPassword(id, password),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['external-user', id] });
      queryClient.invalidateQueries({ queryKey: ['external-users'] });
    },
  });
};

export const useResendWelcomeEmail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => externalUsersApi.resendWelcomeEmail(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['external-user', id] });
    },
  });
};
